import type { ReviewScene } from "./reviewScene";
import type { ReviewViewer } from "../review/reviewViewer";
import type { PlannerController, PlannerHost } from "../planner/types";

export interface PlannerIntegrationOptions {
  mount: HTMLElement;
  viewer: ReviewViewer;
  reviewScene: ReviewScene;
  onVisibilityChange?: (visible: boolean) => void;
  onSelectionChange?: (id: string | null) => void;
  onToast?: (message: string, tone?: "info" | "warning" | "error") => void;
}

export async function createPlannerIntegration(
  options: PlannerIntegrationOptions,
): Promise<PlannerController> {
  const { createPlanner } = await import("../planner/index");
  const { reviewScene, viewer } = options;
  const host: PlannerHost = {
    mount: options.mount,
    coordinateRoot: viewer.registry.coordinateRoot,
    camera: viewer.camera,
    domElement: viewer.renderer.domElement,
    reviewScene: {
      project_id: reviewScene.project_id,
      revision: reviewScene.revision,
      units: reviewScene.units,
      placement_surfaces: reviewScene.placement_surfaces,
      stud_mount_lines: reviewScene.stud_mount_lines,
      hard_exclusion_volumes: reviewScene.hard_exclusion_volumes,
    },
    coreBasis: {
      projectId: reviewScene.project_id,
      revision: reviewScene.revision,
      hash: reviewScene.layers.core_interior.asset.sha256,
    },
    initialVisible: false,
    initialActive: false,
    setPreviewMode: (mode) => {
      options.mount.dataset.previewMode = mode;
      viewer.setViewportLeftInset(mode === "three" ? 0 : () => (
        options.mount.querySelector<HTMLElement>(".furnishings-planner")?.getBoundingClientRect().width ?? 0
      ));
    },
    onTransformingChange: (active) => viewer.setOrbitInteractionEnabled(!active),
    ...(options.onVisibilityChange ? { onVisibilityChange: options.onVisibilityChange } : {}),
    ...(options.onSelectionChange ? { onSelectionChange: options.onSelectionChange } : {}),
    ...(options.onToast ? { onToast: options.onToast } : {}),
  };
  return createPlanner(host);
}
